import {
  Box,
  Button,
  CircularProgress,
  createStyles,
  makeStyles,
  Radio,
  FormControlLabel,
  RadioGroup,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import FormAlert from "legacy-components/form/container/FormAlert";
import { InputContainer } from "modus/components/form/input-container/InputContainer";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import React, { useEffect, useState } from "react";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import { useResourceAppVersionDeploymentCreator } from "../../components/ApiDeploymentCreator";
import { TrimbleColors } from "legacy-components/TrimbleColors";
import { parseInteger } from "legacy-components/form/validator/Validator";
import { notBlank } from "libs/utils/shared/common";
import type { RegionGroup, RegionItemFromApi } from "../../config/deploymentRegions";
import {
  regionItemsToRegionGroups,
  filterRegionsByProvider,
  isRegionAlreadyDeployed,
  isGlobalAlreadyDeployed,
  buildOccupiedRegionalGroupKeys,
  isRegionGreyedOutByOccupiedRegionalGroup,
  getGatewayBrandNameFromGatewayRegionKey,
} from "../../config/deploymentRegions";
import { ApiRegionsService } from "../../services/ApiService";
import type { RegionProvider } from "../../services/ApiService";
import {
  regionalApiStackFromReactEnv,
  resolveRegionalConsumptionHost,
  resolveGlobalConsumptionHost,
  getGatewayBrandNameFromConsumptionSelectors,
} from "../../config/regionalConsumptionHosts";
import clsx from "clsx";

const useStyles = makeStyles((theme) =>
  createStyles({
    formContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    submitLoader: {
      position: "absolute",
      zIndex: 2,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.32)",
    },
    sectionHeading: {
      fontWeight: 600,
      fontSize: 14,
      lineHeight: "20px",
      color: "#252A2E",
      marginBottom: theme.spacing(1.5),
    },
    sectionBlock: {
      marginBottom: theme.spacing(3),
    },
    regionHelperAlert: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 2),
      backgroundColor: "#F1F1F6",
      border: "1px solid " + TrimbleColors.Gray2,
      borderRadius: 8,
      marginTop: theme.spacing(1),
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
    },
    regionHelperText: {
      fontSize: 14,
      lineHeight: "20px",
      color: "#333333",
    },
    regionHelperIcon: {
      fontSize: 20,
      color: TrimbleColors.Gray8,
      flexShrink: 0,
    },
    providerRadioGroup: {
      marginTop: theme.spacing(1),
      flexDirection: "row",
      gap: theme.spacing(2),
    },
    radioLabel: {
      fontSize: 14,
      lineHeight: "20px",
      color: "#252A2E",
    },
    radioRoot: {
      color: TrimbleColors.Gray5,
      "&.Mui-checked": { color: theme.palette.primary.main },
    },
    accordionRoot: {
      "&:before": { display: "none" },
      boxShadow: "none",
      marginBottom: theme.spacing(1),
      backgroundColor: "transparent",
    },
    accordionSummary: {
      minHeight: 48,
      "&.Mui-expanded": { minHeight: 48 },
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      backgroundColor: "#F1F1F6",
      color: "#252A2E",
      borderRadius: "4px 4px 0 0",
      "&:not(.Mui-expanded)": { borderRadius: 4 },
      "& .MuiIconButton-root": { color: TrimbleColors.Gray7 },
    },
    accordionSummaryContent: { margin: "12px 0" },
    accordionSummaryLabel: {
      fontWeight: 500,
      fontSize: 14,
      lineHeight: "20px",
      color: "#252A2E",
    },
    accordionDetails: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      flexDirection: "column",
      backgroundColor: "#F1F1F6",
      borderRadius: "0 0 4px 4px",
    },
    regionRadioGroup: {
      marginTop: 0,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
    regionRadioOption: {
      backgroundColor: TrimbleColors.White,
      border: `1px solid ${TrimbleColors.Gray2}`,
      borderRadius: 4,
      padding: theme.spacing(1.5, 2),
      margin: 0,
    },
    regionRadioOptionSelected: {
      backgroundColor: TrimbleColors.BluePale ?? "#E3F2FD",
      border: `1px solid ${theme.palette.primary.main}`,
    },
    regionRadioOptionDisabled: {
      opacity: 0.55,
      backgroundColor: TrimbleColors.Gray1,
      cursor: "not-allowed",
    },
    consumptionEndpointLabel: {
      fontWeight: 600,
      fontSize: 14,
      lineHeight: "20px",
      color: "#252A2E",
      marginBottom: theme.spacing(1),
      display: "block",
    },
    /** Applied via InputProps.classes.root (read-only, not disabled — MUI disabled opacity washes out #F1F1F6). */
    consumptionEndpointOutlinedRoot: {
      borderRadius: 4,
      backgroundColor: "#F1F1F6",
      cursor: "default",
      "&:hover": { backgroundColor: "#F1F1F6" },
      "&.Mui-focused": { backgroundColor: "#F1F1F6" },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: TrimbleColors.Gray2,
        borderWidth: 1,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: TrimbleColors.Gray2,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: TrimbleColors.Gray2,
        borderWidth: 1,
      },
    },
    consumptionEndpointOutlinedInput: {
      backgroundColor: "#F1F1F6",
      color: TrimbleColors.Gray6,
      WebkitTextFillColor: TrimbleColors.Gray6,
      cursor: "default",
    },
    consumptionEndpointOutlinedInputPlaceholderMode: {
      fontStyle: "italic",
      "&::placeholder": {
        color: TrimbleColors.Gray6,
        fontStyle: "italic",
        opacity: 1,
      },
    },
    connectionHelperText: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
      fontSize: 12,
      lineHeight: "16px",
      color: TrimbleColors.Gray6,
    },
    assignedGatewayTitle: {
      fontWeight: 700,
      fontSize: 40 / 3,
      lineHeight: "20px",
      color: "#252A2E",
    },
    assignedGatewayHelper: {
      fontSize: 12,
      lineHeight: "18px",
      color: "#252A2E",
    },
    assignedGatewayAlert: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 2),
      borderRadius: 12,
      backgroundColor: "#EFF7E8",
      border: "2px solid #027A48",
      borderLeftWidth: 6,
    },
    assignedGatewayIcon: {
      color: "#1D2939",
      fontSize: 22,
      marginTop: 1,
      flexShrink: 0,
    },
    assignedGatewayContent: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.25),
      minWidth: 0,
    },
    actions: {
      borderTop: `1px solid ${TrimbleColors.Gray2}`,
      paddingTop: theme.spacing(3),
      marginTop: theme.spacing(3),
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(2),
    },
    deploymentTypeRadioGroup: {
      border: `1px solid ${TrimbleColors.Gray8}`,
      borderRadius: 12,
      padding: theme.spacing(1),
      marginLeft: theme.spacing(0.5),
      marginTop: theme.spacing(0.7),
    },
    deploymentTypeRadioGroupDisabled: {
      borderColor: TrimbleColors.Gray4,
      backgroundColor: TrimbleColors.Gray1,
    },
    deploymentTypeDisabledText: {
      color: TrimbleColors.Gray6,
    },
    cancelButton: { textTransform: "none" },
    submitButton: { textTransform: "none" },
    submitButtonProgress: { marginRight: theme.spacing(1) },
  })
);

export interface CreateDeploymentFormRegionalProps {
  onCancel: () => void;
  onSuccess?: () => void;
  basePath: string;
  resourceAppId: string;
  version: string;
  regionGroups?: RegionGroup[];
  /** Target regions already used for this version — disabled in the picker */
  deployedRegions?: string[];
}

const unusedLegacyDeployStub = {
  backendUrl: "",
  region: "",
  timeout: 30,
};

type DeploymentType = "REGIONAL" | "GLOBAL";

const CreateDeploymentFormRegional: React.FC<CreateDeploymentFormRegionalProps> = ({
  onCancel,
  onSuccess,
  basePath,
  resourceAppId,
  version,
  regionGroups: regionGroupsProp,
  deployedRegions = [],
}) => {
  const styles = useStyles();
  const [success, setSuccess] = useState(false);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState({ is_error: false, message: "" });
  const [formValues, setFormValues] = useState({
    targetEndpoint: "",
    timeout: "30",
  });
  const [hasFieldError2, setHasFieldError2] = useState(false);
  const [deploymentType, setDeploymentType] = useState<DeploymentType>("REGIONAL");
  const [infrastructureProvider, setInfrastructureProvider] = useState<RegionProvider | "">("");
  const [region, setRegion] = useState<string>("");
  const isRegionalDeployment = deploymentType === "REGIONAL";

  /** All providers — needed so regionalGroup occupancy applies across AWS/Azure */
  const { state: regionsState, subscribe: fetchAllRegionsCatalog } = useAsyncAction(() =>
    ApiRegionsService.getRegions(undefined)
  );

  useEffect(() => {
    if (
      isRegionalDeployment &&
      (infrastructureProvider === "AWS" || infrastructureProvider === "AZURE")
    ) {
      fetchAllRegionsCatalog();
    }
  }, [isRegionalDeployment, infrastructureProvider, fetchAllRegionsCatalog]);

  const allRegionsCatalog = React.useMemo((): RegionItemFromApi[] => {
    const v = regionsState?.value;
    return Array.isArray(v) ? v : [];
  }, [regionsState?.value]);

  const regionGroupsFromApi: RegionGroup[] = React.useMemo(() => {
    if (!allRegionsCatalog.length) return [];
    const filtered =
      infrastructureProvider === "AWS" || infrastructureProvider === "AZURE"
        ? filterRegionsByProvider(allRegionsCatalog, infrastructureProvider)
        : allRegionsCatalog;
    return regionItemsToRegionGroups(filtered);
  }, [allRegionsCatalog, infrastructureProvider]);

  const occupiedRegionalGroupKeys = React.useMemo(
    () => buildOccupiedRegionalGroupKeys(deployedRegions, allRegionsCatalog),
    [deployedRegions, allRegionsCatalog]
  );

  const globalAlreadyDeployed = React.useMemo(
    () => isGlobalAlreadyDeployed(deployedRegions),
    [deployedRegions]
  );

  const regionGroups =
    infrastructureProvider === "AWS" || infrastructureProvider === "AZURE"
      ? regionGroupsFromApi
      : regionGroupsProp?.length
        ? regionGroupsProp
        : regionGroupsFromApi;
  const regionsLoading = regionsState?.loading ?? false;

  const allRegionOptions = React.useMemo(
    () => regionGroups.flatMap((g) => g.regions),
    [regionGroups]
  );

  useEffect(() => {
    if (!region || !deployedRegions.length) return;
    const opt = allRegionOptions.find((r) => r.value === region);
    if (!opt) return;
    if (isRegionAlreadyDeployed(opt, deployedRegions)) {
      setRegion("");
      return;
    }
    if (
      isRegionGreyedOutByOccupiedRegionalGroup(
        opt,
        occupiedRegionalGroupKeys,
        deployedRegions
      )
    ) {
      setRegion("");
    }
  }, [region, deployedRegions, allRegionOptions, occupiedRegionalGroupKeys]);

  useEffect(() => {
    if (globalAlreadyDeployed && deploymentType === "GLOBAL") {
      setDeploymentType("REGIONAL");
    }
  }, [globalAlreadyDeployed, deploymentType]);

  const showRegionSelection =
    isRegionalDeployment &&
    (infrastructureProvider === "AWS" || infrastructureProvider === "AZURE") &&
    regionGroups.length > 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSetError2 = (value: boolean) => setHasFieldError2(value);

  const handleProviderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as RegionProvider | "";
    setInfrastructureProvider(value);
    setRegion("");
  };

  const handleDeploymentTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as DeploymentType;
    setDeploymentType(value);
    if (value === "GLOBAL") {
      setInfrastructureProvider("");
      setRegion("");
    }
  };

  const selectedRegionDisplayName = regionGroups
    ?.flatMap((g) => g.regions)
    .find((r) => r.value === region)?.key;
  const normalizedBasePath = basePath?.replace(/^\/+/, "") ?? "";

  const selectedRegionOption = React.useMemo(
    () => allRegionOptions.find((r) => r.value === region),
    [allRegionOptions, region]
  );

  const gatewayBrand = React.useMemo(() => {
    if (!region) return "";
    if (selectedRegionOption) {
      const fromApi = getGatewayBrandNameFromConsumptionSelectors(
        selectedRegionOption.basePathIdentifier,
        selectedRegionOption.regionalGroup
      );
      if (fromApi) return fromApi;
    }
    return getGatewayBrandNameFromGatewayRegionKey(region);
  }, [region, selectedRegionOption]);

  const regionalConsumptionStack = React.useMemo(
    () => regionalApiStackFromReactEnv(process.env.REACT_APP_ENVIRONMENT),
    []
  );

  const regionalConsumptionHost = React.useMemo(
    () =>
      resolveRegionalConsumptionHost(
        selectedRegionOption?.basePathIdentifier,
        regionalConsumptionStack,
        selectedRegionOption?.regionalGroup
      ),
    [
      selectedRegionOption?.basePathIdentifier,
      selectedRegionOption?.regionalGroup,
      regionalConsumptionStack,
    ]
  );

  const globalConsumptionHost = React.useMemo(
    () => resolveGlobalConsumptionHost(regionalConsumptionStack),
    [regionalConsumptionStack]
  );

  /** Host from {@link regionalConsumptionHosts} when regionalGroup + env match; else legacy placeholder. */
  const consumptionEndpointPreview = React.useMemo(() => {
    if (!normalizedBasePath || !version) return "";
    const path = `${normalizedBasePath}/${version}`;
    if (!isRegionalDeployment) {
      if (globalConsumptionHost) {
        return `https://${globalConsumptionHost}/${path}`;
      }
      return `https://<domain>/${path}`;
    }
    if (!region) return "";
    if (regionalConsumptionHost) {
      return `https://${regionalConsumptionHost}/${path}`;
    }
    return `https://<domain>/${path}`;
  }, [
    isRegionalDeployment,
    region,
    normalizedBasePath,
    version,
    regionalConsumptionHost,
    globalConsumptionHost,
  ]);

  const consumptionEndpointPlaceholder = React.useMemo(() => {
    if (!isRegionalDeployment) {
      return "";
    }
    if (!infrastructureProvider) {
      return "Select infrastructure provider first…";
    }
    if (regionsLoading && (infrastructureProvider === "AWS" || infrastructureProvider === "AZURE")) {
      return "Loading regions…";
    }
    if (!region) {
      return "Waiting for region selection…";
    }
    return "";
  }, [isRegionalDeployment, infrastructureProvider, regionsLoading, region]);

  const deployV2Request = React.useMemo(
    () => ({
      provider: (isRegionalDeployment &&
      (infrastructureProvider === "AWS" || infrastructureProvider === "AZURE")
        ? infrastructureProvider
        : "AZURE") as "AWS" | "AZURE",
      backendUrl: formValues.targetEndpoint,
      targetRegion: isRegionalDeployment
        ? selectedRegionDisplayName ?? region
        : "global",
      timeout: notBlank(formValues.timeout) ? parseInteger(formValues.timeout) : 30,
    }),
    [
      isRegionalDeployment,
      infrastructureProvider,
      formValues.targetEndpoint,
      formValues.timeout,
      selectedRegionDisplayName,
      region,
    ]
  );

  const { createDeploymentstate, createVersionDeployment } =
    useResourceAppVersionDeploymentCreator(
      resourceAppId,
      version,
      true,
      deployV2Request,
      unusedLegacyDeployStub
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createVersionDeployment();
  };

  useEffect(() => {
    if (createDeploymentstate?.loaded && !createDeploymentstate?.error && onSuccess) onSuccess();
  }, [createDeploymentstate?.loaded, createDeploymentstate?.error, createDeploymentstate?.value, onSuccess]);

  return (
    <form
      className={styles.formContainer}
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      {loader && <Box className={styles.submitLoader} />}
      {error.is_error && <FormAlert severity="error">{error.message}</FormAlert>}
      {success && (
        <FormAlert severity="success">
          Deployment started successfully.
        </FormAlert>
      )}
      {createDeploymentstate?.error && (
        <FormAlert severity="error">
          {resolveAPIErrorMessage(createDeploymentstate.error)}
        </FormAlert>
      )}

      <Box className={styles.sectionBlock}>
        <Typography className={styles.sectionHeading}>
          Connection settings
        </Typography>
        <InputContainer
          label="Backend URL"
          value={formValues.targetEndpoint}
          onChange={handleChange}
          name="targetEndpoint"
          setError={handleSetError2}
          validate={{
            minLength: 3,
            maxLength: 255,
            required: true,
            pattern:
              "^(http:\\/\\/www\\.|https:\\/\\/www\\.|http:\\/\\/|https:\\/\\/)[a-z0-9]+([-.]{1}[a-z0-9]+)*\\.[a-z]{2,5}(:[0-9]{1,5})?(\\/[^?]*)?(\\?([a-zA-Z0-9_-]+=[a-zA-Z0-9_-]+)(&[a-zA-Z0-9_-]+=[a-zA-Z0-9_-]+)*)?$",
          }}
          required
        />
        <Typography className={styles.connectionHelperText}>
          The destination URL where your API is hosted. e.g.,
          https://api.example.com
        </Typography>
        <InputContainer
          label="Connect timeout"
          placeholder="Enter the connection timeout"
          value={formValues.timeout}
          onChange={handleChange}
          name="timeout"
          setError={handleSetError2}
          validate={{
            required: true,
            allowSpace: false,
            pattern: "^[0-9]+$",
            min: 1,
            max: 55,
            patternMessage: "Only numeric values are allowed",
          }}
          required
        />
      </Box>

      <Box className={styles.sectionBlock}> 
        <Typography className={styles.sectionHeading}>
          Select your deployment type
        </Typography>
        <RadioGroup
          aria-label="Deployment type"
          name="deploymentType"
          value={deploymentType}
          onChange={handleDeploymentTypeChange}
        >
          <FormControlLabel className={styles.deploymentTypeRadioGroup}
            value="REGIONAL"
            control={
              <Radio size="small" className={styles.radioRoot} color="primary" />
            }
            label={
              <Box>
                <Typography className={styles.radioLabel}>Regional</Typography>
                <Typography className={styles.connectionHelperText}>
                  Redirect API to a specific cloud provider region.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            className={clsx(
              styles.deploymentTypeRadioGroup,
              globalAlreadyDeployed && styles.deploymentTypeRadioGroupDisabled
            )}
            value="GLOBAL"
            disabled={globalAlreadyDeployed}
            control={
              <Radio
                size="small"
                className={styles.radioRoot}
                color="primary"
                disabled={globalAlreadyDeployed}
              />
            }
            label={
              <Box>
                <Typography
                  className={clsx(
                    styles.radioLabel,
                    globalAlreadyDeployed && styles.deploymentTypeDisabledText
                  )}
                >
                  Global
                </Typography>
                <Typography
                  className={clsx(
                    styles.connectionHelperText,
                    globalAlreadyDeployed && styles.deploymentTypeDisabledText
                  )}
                >
                  {globalAlreadyDeployed
                    ? "Global deployment already exists for this version."
                    : "General endpoint with no regional targeting."}
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </Box>

      {isRegionalDeployment ? (
      <>
      <Box className={styles.sectionBlock}>
        <Typography className={styles.sectionHeading}>
        Where is the target API deployed?
        </Typography>
        <RadioGroup
          aria-label="Infrastructure provider"
          name="infrastructureProvider"
          value={infrastructureProvider}
          onChange={handleProviderChange}
          className={styles.providerRadioGroup}
        >
          <FormControlLabel
            value="AWS"
            control={
              <Radio size="small" className={styles.radioRoot} color="primary" />
            }
            label="AWS"
            classes={{ label: styles.radioLabel }}
          />
          <FormControlLabel
            value="AZURE"
            control={
              <Radio size="small" className={styles.radioRoot} color="primary" />
            }
            label="Azure"
            classes={{ label: styles.radioLabel }}
          />
        </RadioGroup>
        {!infrastructureProvider ? (
          <Box className={styles.regionHelperAlert}>
            <InfoOutlinedIcon className={styles.regionHelperIcon} />
            <Typography className={styles.regionHelperText}>
              Select an infrastructure provider to view available regions.
            </Typography>
          </Box>
        ) : null}
      </Box>

      {infrastructureProvider === "AWS" || infrastructureProvider === "AZURE" ? (
        <Box className={styles.sectionBlock}>
          {regionsLoading ? (
            <Typography className={styles.regionHelperText} style={{ padding: 16 }}>
              Loading regions…
            </Typography>
          ) : (
            regionGroups?.map((group, index) => (
              <Accordion
                key={group.label}
                defaultExpanded={index === 0}
                classes={{ root: styles.accordionRoot }}
                elevation={0}
                square
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  classes={{
                    root: styles.accordionSummary,
                    content: styles.accordionSummaryContent,
                  }}
                  aria-controls={`region-${group.label}-content`}
                  id={`region-${group.label}-header`}
                >
                  <Typography className={styles.accordionSummaryLabel}>
                    {group.label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails classes={{ root: styles.accordionDetails }}>
                  <RadioGroup
                    aria-label={`Region: ${group.label}`}
                    name="region"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className={styles.regionRadioGroup}
                  >
                    {group.regions.map((r) => {
                      const deployed = isRegionAlreadyDeployed(r, deployedRegions);
                      const blockedByGroup = isRegionGreyedOutByOccupiedRegionalGroup(
                        r,
                        occupiedRegionalGroupKeys,
                        deployedRegions
                      );
                      const disabled = deployed || blockedByGroup;
                      return (
                        <Box
                          key={r.value}
                          className={`${styles.regionRadioOption} ${
                            region === r.value ? styles.regionRadioOptionSelected : ""
                          } ${disabled ? styles.regionRadioOptionDisabled : ""}`.trim()}
                        >
                          <FormControlLabel
                            value={r.value}
                            disabled={disabled}
                            control={
                              <Radio
                                size="small"
                                className={styles.radioRoot}
                                color="primary"
                                disabled={disabled}
                              />
                            }
                            label={r.key}
                            classes={{ label: styles.radioLabel }}
                          />
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </AccordionDetails>
              </Accordion>
            ))
          )}
          {showRegionSelection && !region && (
            <Box className={styles.regionHelperAlert} style={{ marginTop: 16 }}>
              <InfoOutlinedIcon className={styles.regionHelperIcon} />
              <Typography className={styles.regionHelperText}>
                Select a region to determine the optimal API gateway.
              </Typography>
            </Box>
          )}
        </Box>
      ) : null}

      {region && (
        <Box className={styles.sectionBlock}>
          <Box className={styles.assignedGatewayAlert}>
            <CheckCircleOutlineIcon className={styles.assignedGatewayIcon} />
            <Box className={styles.assignedGatewayContent}>
              <Typography className={styles.assignedGatewayTitle}>
                {gatewayBrand} Gateway
              </Typography>
              <Typography className={styles.assignedGatewayHelper}>
                The optimal gateway has been selected to minimize latency.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      </>
      ) : null}
      <Box className={styles.sectionBlock}>
        <Typography
          className={styles.consumptionEndpointLabel}
          component="label"
          htmlFor="consumption-endpoint"
        >
          Consumption endpoint
        </Typography>
        <TextField
          id="consumption-endpoint"
          value={consumptionEndpointPreview.toLowerCase()}
          placeholder={consumptionEndpointPlaceholder.toLowerCase()}
          fullWidth
          variant="outlined"
          size="small"
          InputProps={{
            readOnly: true,
            classes: {
              root: styles.consumptionEndpointOutlinedRoot,
              input: clsx(
                styles.consumptionEndpointOutlinedInput,
                !consumptionEndpointPreview &&
                  styles.consumptionEndpointOutlinedInputPlaceholderMode
              ),
            },
          }}
          inputProps={{
            "aria-label": "Consumption endpoint",
            "aria-describedby": "consumption-endpoint-helper",
            "aria-readonly": "true",
          }}
        />
      </Box>

      <Box className={styles.actions}>
        <Button
          type="button"
          variant="outlined"
          onClick={onCancel}
          className={styles.cancelButton}
          disableElevation
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={styles.submitButton}
          disableElevation
          disabled={
            hasFieldError2 ||
            !formValues.targetEndpoint ||
            createDeploymentstate?.loading ||
            !basePath ||
            (isRegionalDeployment &&
              (!region ||
                (infrastructureProvider !== "AWS" &&
                  infrastructureProvider !== "AZURE")))
          }
        >
          {createDeploymentstate?.loading ? (
            <>
              <CircularProgress
                size={18}
                color="inherit"
                className={styles.submitButtonProgress}
                aria-hidden
              />
              Adding deployment…
            </>
          ) : (
            "Add deployment"
          )}
        </Button>
      </Box>
    </form>
  );
};

export default CreateDeploymentFormRegional;
